import WeakValueMap from './WeakValueMap';

export function memoizeWeakKey<K extends WeakKey, V, This = unknown>(
  fn: (this: This, key: K) => V,
): {
  (this: This, key: K): V;
  get cache(): WeakMap<K, V>;
} {
  const cache = new WeakMap<K, V>();

  const memoizedFnName = fn.name === '' ? 'memoized' : `${fn.name}Memoized`;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const memoizedFn = {
    [memoizedFnName](this: This, key: K): V {
      let value = cache.get(key);
      if (value === undefined) {
        value = fn.call(this, key);
        cache.set(key, value);
      }
      return value;
    },
  }[memoizedFnName]!;

  return Object.assign(memoizedFn, { cache });
}

function isWeakKey<T>(value: T): value is Extract<T, WeakKey> {
  switch (typeof value) {
    case 'object':
      return value !== null;
    case 'function':
      // Functions are "object-ish"
      return true;
    case 'symbol':
      // Registered symbols can be weakly held
      return Symbol.keyFor(value) !== undefined;
    default:
      return false;
  }
}

export function memoizeWeakValue<K, V, This = unknown>(
  fn: (this: This, key: K) => V,
): {
  (this: This, key: K): V;
  get cache(): WeakValueMap<K, Extract<V, WeakKey>>;
} {
  const cache = new WeakValueMap<K, Extract<V, WeakKey>>();

  const memoizedFnName = fn.name === '' ? 'memoized' : `${fn.name}Memoized`;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const memoizedFn = {
    [memoizedFnName](this: This, key: K): V {
      let value: V | undefined = cache.get(key);
      if (value === undefined || !cache.has(key)) {
        value = fn.call(this, key);
        // Only cache the value if it can be weakly held
        if (isWeakKey(value)) {
          cache.set(key, value);
        }
      }
      return value;
    },
  }[memoizedFnName]!;

  return Object.assign(memoizedFn, { cache });
}
